// Real-time Tracking Hook for Mercapoli Move
// Handles WebSocket connection for live location updates
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth-context';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 5;
const PING_INTERVAL = 30000;

export const useTracking = (serviceId, options = {}) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [session, setSession] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [eta, setEta] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef(null);
  
  const { onLocationUpdate, onStatusChange, onSessionEnd, autoConnect = true } = options;
  
  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert https to wss, http to ws
    const wsUrl = backendUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
    return `${wsUrl}/ws/tracking/${serviceId}?user_id=${user?.id || 'anonymous'}`;
  }, [serviceId, user?.id]);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!serviceId || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    try {
      const wsUrl = getWsUrl();
      console.log('[Tracking] Connecting to:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('[Tracking] Connected');
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[Tracking] Message:', data.type);
          
          switch (data.type) {
            case 'session_state':
            case 'session_created':
              setSession(data.session);
              setStatus(data.session?.status);
              if (data.session?.current_location) {
                setDriverLocation(data.session.current_location);
              }
              if (data.session?.eta_minutes !== undefined) {
                setEta(data.session.eta_minutes);
              }
              break;
            
            case 'location_update':
              setDriverLocation(data.location);
              if (data.eta_minutes !== undefined) {
                setEta(data.eta_minutes);
              }
              if (data.status) {
                setStatus(data.status);
              }
              onLocationUpdate?.(data.location, data.eta_minutes);
              break;
            
            case 'status_update':
              setStatus(data.status);
              onStatusChange?.(data.status);
              break;
            
            case 'session_ended':
              setSession(null);
              setDriverLocation(null);
              setEta(null);
              setStatus(null);
              onSessionEnd?.();
              break;
            
            case 'pong':
              // Ping response, connection is alive
              break;
            
            default:
              console.log('[Tracking] Unknown message type:', data.type);
          }
        } catch (err) {
          console.error('[Tracking] Error parsing message:', err);
        }
      };
      
      ws.onerror = (event) => {
        console.error('[Tracking] WebSocket error:', event);
        setError('Connection error');
      };
      
      ws.onclose = (event) => {
        console.log('[Tracking] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Attempt reconnect
        if (reconnectAttempts.current < WS_MAX_RECONNECT_ATTEMPTS && autoConnect) {
          reconnectAttempts.current++;
          console.log(`[Tracking] Reconnecting... attempt ${reconnectAttempts.current}`);
          setTimeout(connect, WS_RECONNECT_DELAY);
        } else if (reconnectAttempts.current >= WS_MAX_RECONNECT_ATTEMPTS) {
          setError('Connection lost. Please refresh the page.');
        }
      };
    } catch (err) {
      console.error('[Tracking] Connection error:', err);
      setError('Failed to connect');
    }
  }, [serviceId, getWsUrl, onLocationUpdate, onStatusChange, onSessionEnd, autoConnect]);
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    setIsConnected(false);
  }, []);
  
  // Send location update (for drivers)
  const sendLocationUpdate = useCallback((location, etaMinutes) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'location_update',
        ...location,
        eta_minutes: etaMinutes
      }));
    }
  }, []);
  
  // Send status update
  const sendStatusUpdate = useCallback((newStatus) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'status_update',
        status: newStatus
      }));
    }
  }, []);
  
  // Auto-connect on mount
  useEffect(() => {
    if (serviceId && autoConnect) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [serviceId, autoConnect, connect, disconnect]);
  
  return {
    isConnected,
    session,
    driverLocation,
    eta,
    status,
    error,
    connect,
    disconnect,
    sendLocationUpdate,
    sendStatusUpdate
  };
};

// Hook for tracking multiple services (admin view)
export const useTrackingStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tracking/stats`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching tracking stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, [fetchStats]);
  
  return { stats, loading, refetch: fetchStats };
};

export default useTracking;
